import { defineStyleConfig } from "@chakra-ui/react";
import type { SystemStyleFunction } from "@chakra-ui/theme-tools";
import { merge } from "es-toolkit/compat";

const linkSizeVariants: (theme: any) => { [key in ButtonSizes]: any } = (
  theme: any,
) => ({
  sm: theme.textStyles.captionLink,
  md: theme.textStyles.buttonLinkM,
  lg: theme.textStyles.buttonLinkL,
});

const variantPrimary: SystemStyleFunction = () => ({
  color: "fg.inverted",
  bg: "button.solid",
  borderColor: "button.solid",
  _hover: {
    bg: "button.emphasized",
    borderColor: "button.emphasized",
    _disabled: {
      bg: "button.subtle",
      borderColor: "button.subtle",
    },
  },
  _focusVisible: {
    bg: "button.emphasized",
    borderColor: "button.solid",
  },
  _disabled: {
    borderColor: "button.subtle",
    color: "fg.subtle",
    bg: "button.subtle",
  },
});

const variantInverted: SystemStyleFunction = () => ({
  color: "button.inverted.default.fg",
  bg: "button.inverted.default.bg",
  borderColor: "button.inverted.default.border",
  _hover: {
    color: "button.inverted.hover.fg",
    bg: "button.inverted.hover.bg",
    borderColor: "button.inverted.hover.border",
    _disabled: {
      bg: "button.inverted.disabled.bg",
      borderColor: "button.inverted.disabled.border",
    },
  },
  _focusVisible: {
    color: "button.inverted.focus.fg",
    bg: "button.inverted.focus.bg",
    borderColor: "button.inverted.focus.border",
  },
  _disabled: {
    color: "button.inverted.disabled.fg",
    bg: "button.inverted.disabled.bg",
    borderColor: "button.inverted.disabled.border",
  },
});

const variantSecondary: SystemStyleFunction = () => ({
  color: "fg.default",
  bg: "transparent",
  borderColor: "button.solid",
  _hover: {
    bg: "button.default",
    _disabled: {
      bg: "transparent",
    },
  },
  _focusVisible: {
    bg: "button.default",
  },
  _disabled: {
    borderColor: "button.muted",
    color: "button.muted",
  },
});

const variantGhost: SystemStyleFunction = () => ({
  color: "highlight.fg",
  bg: "transparent",
  borderColor: "transparent",
  _hover: {
    bg: "highlight.subtle",
    _disabled: {
      bg: "button.default",
    },
  },
  _focusVisible: {
    bg: "highlight.subtle",
    borderColor: "highlight.focusRing",
  },
  _disabled: {
    color: "fg.subtle",
  },
});

const variantLink: SystemStyleFunction = ({
  theme,
  size = "sm",
}: {
  theme: any;
  size?: ButtonSizes;
}) => ({
  py: "2px",
  color: "highlight.fg",
  borderColor: "transparent",
  _hover: {
    _disabled: {
      textDecorationLine: "underline",
    },
  },
  _active: {
    color: "highlight.fg",
    _disabled: {
      color: "fg.subtle",
    },
  },
  _focusVisible: {
    textDecorationThickness: "2px",
  },
  _disabled: {
    color: "fg.subtle",
  },
  ...linkSizeVariants(theme)[size],
});

const variantDanger: SystemStyleFunction = (props) =>
  merge(variantPrimary(props), {
    bg: "error.solid",
    borderColor: "error.solid",
    color: "fg.contrast",
    _hover: {
      bg: "error.emphasized",
    },
    _focusVisible: {
      bg: "error.emphasized",
      borderColor: "error.focusRing",
    },
    _disabled: {
      bg: "button.subtle",
      color: "fg.subtle",
    },
  });

export const buttonVariants = {
  primary: variantPrimary,
  inverted: variantInverted,
  danger: variantDanger,
  secondary: variantSecondary,
  ghost: variantGhost,
  link: variantLink,
};

export const Button = defineStyleConfig({
  baseStyle: {
    px: "4",
    borderRadius: "sm",
    width: "fit-content",

    _hover: {
      _disabled: {},
    },

    _active: {
      transform: "translateY(1px)",
      transition: (theme) =>
        `transform ${theme.transition.duration.fast} ${theme.transition.easing.easeOut}`,

      _disabled: {
        transform: "translateY(0)",
      },
    },

    _focus: {
      boxShadow: "none",
    },

    _disabled: {
      cursor: "default",
    },
  },

  sizes: {
    lg: ({ theme }) => ({
      py: "2",
      px: "4",
      h: "10",
      minW: "120px",
      maxW: "320px",
      borderWidth: "2px",
      ...theme.textStyles.buttonL,

      _focusVisible: {
        borderWidth: "3px",

        _disabled: {
          borderWidth: "2px",
        },
      },
    }),
    md: ({ theme }) => ({
      h: "8",
      minW: "100px",
      maxW: "260px",
      borderWidth: "1px",
      ...theme.textStyles.buttonM,

      _focusVisible: {
        borderWidth: "2px",

        _disabled: {
          borderWidth: "1px",
        },
      },
    }),
    sm: ({ theme }) => ({
      h: "6",
      minW: "62px",
      maxW: "200px",
      borderWidth: "1px",
      px: "2",
      ...theme.textStyles.caption,

      _focusVisible: {
        borderWidth: "2px",

        _disabled: {
          borderWidth: "1px",
        },
      },

      ".chakra-button__icon > div": {
        height: "3",
      },

      ".chakra-button__icon svg": {
        width: "3",
        height: "3",
      },
    }),
  },

  variants: buttonVariants,
});

export type ButtonSizes = keyof Exclude<(typeof Button)["sizes"], undefined>;

export type ButtonVariants = keyof Exclude<
  (typeof Button)["variants"],
  undefined
>;
